// app/javascript/controllers/popover_controller.js
import { Controller } from "@hotwired/stimulus"
import { Popover } from "bootstrap"
import { announcePopoverOpen, bindExclusivePopover } from "../utils/exclusive-popover.util"
import { cancelPopoverClose, closeWithPopoverMotion } from "../utils/popover-motion.util"

export default class extends Controller {
  static values = {
    html: { type: Boolean, default: true },
    delay: { type: Number, default: 100 },
    placement: { type: String, default: "top" },
    class: String,
    title: String,
    content: String,
    // When set, popover body HTML is taken from this element (server-rendered, trusted).
    contentSourceSelector: String,
    // Popper offset [0, n] main-axis gap from trigger (Bootstrap default for popover is 8px — large for hover handoff).
    offsetMainAxis: Number,
    // Also open on focus and click/tap; dismiss on outside click and Escape (for keyboard/touch).
    focusClick: { type: Boolean, default: false }
  }

  connect() {
    // Stimulus reuses this controller object across a disconnect/reconnect
    // of the same element (e.g. Turbo morph), so lifecycle flags from a
    // previous instance must not leak into the new one.
    this.hidePending = false
    this.completingAnimatedHide = false
    this.#initializePopover()
    this.unbindExclusivePopover = bindExclusivePopover(this.element, () => this.hide())
    this.#bindEventListeners()
  }

  // Public methods
  show() {
    this.#clearHideTimeout()
    announcePopoverOpen(this.element)
    const tip = this.#getPopoverElement()
    cancelPopoverClose(tip)
    // Already open (or its closing motion was just cancelled): showing again would re-run Bootstrap's completion
    // callback, which sees _isHovered === false after the first one and hides the popover. A mouse click on a
    // focus-click trigger shows twice (focusin, then click), so it closed the popover it had just opened.
    if (tip?.isConnected && tip.classList.contains("show")) return
    this.popover?.show()
  }

  hide() {
    this.#clearHideTimeout()
    this.popover?.hide()
  }

  disconnect() {
    this.#cleanup()
  }

  contentValueChanged() {
    if (!this.popover) return
    let body = this.contentValue || ""
    if (this.hasContentSourceSelectorValue) {
      const source = document.querySelector(this.contentSourceSelectorValue)
      if (source) body = source.innerHTML
    }
    this.popover.setContent({ ".popover-body": body })
  }

  // Private methods
  #initializePopover() {
    let bodyHtml = this.contentValue
    let useHtml = this.htmlValue
    let sanitize = true

    if (this.hasContentSourceSelectorValue) {
      const source = document.querySelector(this.contentSourceSelectorValue)
      if (source) {
        bodyHtml = source.innerHTML
        useHtml = true
        sanitize = false
      }
    }

    const options = {
      trigger: "manual",
      html: useHtml,
      placement: this.placementValue,
      sanitize
    }

    // Add title if provided
    if (this.titleValue) {
      options.title = this.titleValue
    }

    // Add content if provided
    if (bodyHtml) {
      options.content = bodyHtml
    }

    // Add custom class if provided
    if (this.classValue) {
      options.customClass = this.classValue
    }

    if (this.hasOffsetMainAxisValue) {
      options.offset = [0, this.offsetMainAxisValue]
    }

    this.popover = new Popover(this.element, options)
  }

  #bindEventListeners() {
    this.boundHandleMouseEnter = this.#handleMouseEnter.bind(this)
    this.boundHandleMouseLeave = this.#handleMouseLeave.bind(this)
    this.boundHandlePopoverHide = this.#handlePopoverHide.bind(this)
    this.boundHandlePopoverShown = this.#handlePopoverShown.bind(this)
    this.boundHandlePopoverHidden = this.#handlePopoverHidden.bind(this)

    // Main element events
    this.element.addEventListener("mouseenter", this.boundHandleMouseEnter)
    this.element.addEventListener("mouseleave", this.boundHandleMouseLeave)
    this.element.addEventListener("hide.bs.popover", this.boundHandlePopoverHide)
    this.element.addEventListener("shown.bs.popover", this.boundHandlePopoverShown)
    this.element.addEventListener("hidden.bs.popover", this.boundHandlePopoverHidden)

    if (this.focusClickValue) {
      this.boundHandleFocusIn = this.#handleFocusIn.bind(this)
      this.boundHandleFocusOut = this.#handleFocusOut.bind(this)
      this.boundHandleClick = this.#handleTriggerClick.bind(this)
      this.element.addEventListener("focusin", this.boundHandleFocusIn)
      this.element.addEventListener("focusout", this.boundHandleFocusOut)
      this.element.addEventListener("click", this.boundHandleClick)
      // A role="button" span (a trigger that must work inside a disabled fieldset) gets no click from Enter/Space;
      // without this, a popover closed by Escape stays closed until focus leaves and comes back.
      if (!this.element.matches("button, a[href], input")) {
        this.boundHandleKeydown = this.#handleTriggerKeydown.bind(this)
        this.element.addEventListener("keydown", this.boundHandleKeydown)
      }
    }
  }

  #handleTriggerKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return
    event.preventDefault()
    this.show()
  }

  #handleMouseEnter() {
    this.#clearHideTimeout()
    this.show()
  }

  #handleMouseLeave(event) {
    const tip = this.popoverTipElement || this.#getPopoverElement()
    const next = event.relatedTarget
    if (this.#isInsideTriggerOrTip(next, tip)) {
      return
    }
    this.#scheduleHide()
  }

  #handleFocusIn() {
    this.#clearHideTimeout()
    this.show()
  }

  #handleFocusOut(event) {
    const tip = this.popoverTipElement || this.#getPopoverElement()
    const next = event.relatedTarget
    if (this.#isInsideTriggerOrTip(next, tip)) {
      return
    }
    this.#scheduleHide()
  }

  #handleTriggerClick(event) {
    this.#clearHideTimeout()
    this.show()
    if (this.element.getAttribute("aria-disabled") === "true") {
      event.preventDefault()
    }
  }

  #bindDocumentDismissListeners() {
    if (this.documentDismissBound) return
    this.documentDismissBound = true
    this.boundHandleDocumentClick = this.#handleDocumentClick.bind(this)
    this.boundHandleEscape = this.#handleEscape.bind(this)
    document.addEventListener("click", this.boundHandleDocumentClick)
    document.addEventListener("keydown", this.boundHandleEscape, true)
  }

  #unbindDocumentDismissListeners() {
    if (!this.documentDismissBound) return
    document.removeEventListener("click", this.boundHandleDocumentClick)
    document.removeEventListener("keydown", this.boundHandleEscape, true)
    this.documentDismissBound = false
    this.boundHandleDocumentClick = null
    this.boundHandleEscape = null
  }

  #handleDocumentClick(event) {
    if (performance.now() < (this.dismissClickIgnoreUntil || 0)) return
    const tip = this.popoverTipElement || this.#getPopoverElement()
    if (!tip?.classList.contains("show")) return
    if (this.element.contains(event.target) || tip.contains(event.target)) return
    this.hide()
  }

  #handleEscape(event) {
    if (event.key !== "Escape") return
    const tip = this.popoverTipElement || this.#getPopoverElement()
    if (!tip?.classList.contains("show")) return
    event.preventDefault()
    this.hide()
    if (typeof this.element.focus === "function") {
      this.element.focus()
    }
  }

  #handlePopoverHide(event) {
    // Any path that returns here without preventDefault() lets Bootstrap's
    // real hide() proceed, which queues its own async completion callback --
    // mark it pending so a disconnect before that callback fires defers
    // disposal instead of nulling state out from under it (LUC-717).
    if (this.completingAnimatedHide) {
      this.completingAnimatedHide = false
      this.hidePending = true
      return
    }

    const tip = this.popoverTipElement || this.#getPopoverElement()
    if (!tip?.classList.contains("show")) {
      this.hidePending = true
      return
    }

    event.preventDefault()
    closeWithPopoverMotion(tip, () => {
      this.completingAnimatedHide = true
      this.popover?.hide()
    })
  }

  #handlePopoverShown() {
    // Tip + aria-describedby are guaranteed after paint; binding too early misses hover on the popover.
    requestAnimationFrame(() => this.#bindPopoverHoverListeners())
    if (this.focusClickValue) {
      this.dismissClickIgnoreUntil = performance.now() + 200
      requestAnimationFrame(() => {
        if (!this.popover) return
        this.#bindDocumentDismissListeners()
      })
    }
  }

  #handlePopoverHidden() {
    this.hidePending = false
    this.#clearHideTimeout()
    this.#unbindPopoverTipListeners()
    if (this.focusClickValue) {
      this.#unbindDocumentDismissListeners()
    }
  }

  #scheduleHide() {
    this.#clearHideTimeout()
    this.hideTimeout = setTimeout(() => {
      this.#stopPointerWatch()
      const tip = this.popoverTipElement || this.#getPopoverElement()
      const { lastPointerX: x, lastPointerY: y } = this
      const byCoords = this.#pointerInsideInteractiveZones(x, y)
      const byHover =
        (typeof this.element.matches === "function" && this.element.matches(":hover")) ||
        (tip && typeof tip.matches === "function" && tip.matches(":hover"))
      if (this.focusClickValue) {
        const active = document.activeElement
        if (this.#isInsideTriggerOrTip(active, tip)) {
          return
        }
      }
      if (!byCoords && !byHover) {
        this.hide()
      }
    }, this.delayValue)
    this.#startPointerWatch()
  }

  #clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.#stopPointerWatch()
  }

  #startPointerWatch() {
    if (this.pointerWatchBound) return
    this.pointerWatchBound = (event) => {
      this.lastPointerX = event.clientX
      this.lastPointerY = event.clientY
      if (this.#pointerInsideInteractiveZones(event.clientX, event.clientY)) {
        this.#clearHideTimeout()
      }
    }
    document.addEventListener("mousemove", this.pointerWatchBound, { passive: true })
  }

  #stopPointerWatch() {
    if (!this.pointerWatchBound) return
    document.removeEventListener("mousemove", this.pointerWatchBound)
    this.pointerWatchBound = null
  }

  #containsPoint(element, x, y) {
    if (!element || x == null || y == null || Number.isNaN(x) || Number.isNaN(y)) {
      return false
    }
    const rect = element.getBoundingClientRect()
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  #pointerInsideInteractiveZones(x, y) {
    const tip = this.popoverTipElement || this.#getPopoverElement()
    return this.#containsPoint(this.element, x, y) || this.#containsPoint(tip, x, y)
  }

  #isInsideTriggerOrTip(node, tip) {
    if (!(node instanceof Node)) {
      return false
    }
    if (this.element === node || this.element.contains(node)) {
      return true
    }
    if (tip && (tip === node || tip.contains(node))) {
      return true
    }
    return false
  }

  #bindPopoverHoverListeners() {
    this.#unbindPopoverTipListeners()
    const popoverElement = this.#getPopoverElement()
    if (!popoverElement) return

    this.popoverTipElement = popoverElement
    this.boundHandlePopoverMouseEnter = this.#handlePopoverMouseEnter.bind(this)
    this.boundHandlePopoverMouseLeave = this.#handlePopoverMouseLeave.bind(this)
    popoverElement.addEventListener("mouseenter", this.boundHandlePopoverMouseEnter)
    popoverElement.addEventListener("mouseleave", this.boundHandlePopoverMouseLeave)
  }

  #unbindPopoverTipListeners() {
    if (this.popoverTipElement) {
      if (this.boundHandlePopoverMouseEnter) {
        this.popoverTipElement.removeEventListener("mouseenter", this.boundHandlePopoverMouseEnter)
      }
      if (this.boundHandlePopoverMouseLeave) {
        this.popoverTipElement.removeEventListener("mouseleave", this.boundHandlePopoverMouseLeave)
      }
    }
    this.popoverTipElement = null
    this.boundHandlePopoverMouseEnter = null
    this.boundHandlePopoverMouseLeave = null
  }

  #handlePopoverMouseEnter() {
    this.#clearHideTimeout()
  }

  #handlePopoverMouseLeave(event) {
    const next = event.relatedTarget
    if (this.#isInsideTriggerOrTip(next, this.popoverTipElement || this.#getPopoverElement())) {
      return
    }
    this.#scheduleHide()
  }

  #getPopoverElement() {
    const id = this.element.getAttribute("aria-describedby")
    return id ? document.getElementById(id) : null
  }

  #cleanup() {
    this.#clearHideTimeout()
    // Cancel any in-flight custom exit animation so its completion callback
    // (which would otherwise fire later against this now-disposed instance)
    // never runs -- without this, it can resurrect hidePending on a reused
    // controller (Stimulus reconnect) after teardown.
    cancelPopoverClose(this.popoverTipElement || this.#getPopoverElement())
    this.unbindExclusivePopover?.()
    this.#removeEventListeners()
    this.#disposePopover()
  }

  #removeEventListeners() {
    if (!this.element) return

    // Remove main element listeners
    if (this.boundHandleMouseEnter) {
      this.element.removeEventListener("mouseenter", this.boundHandleMouseEnter)
      this.boundHandleMouseEnter = null
    }
    if (this.boundHandleMouseLeave) {
      this.element.removeEventListener("mouseleave", this.boundHandleMouseLeave)
      this.boundHandleMouseLeave = null
    }
    if (this.boundHandlePopoverHide) {
      this.element.removeEventListener("hide.bs.popover", this.boundHandlePopoverHide)
      this.boundHandlePopoverHide = null
    }
    if (this.boundHandlePopoverShown) {
      this.element.removeEventListener("shown.bs.popover", this.boundHandlePopoverShown)
      this.boundHandlePopoverShown = null
    }
    if (this.boundHandlePopoverHidden) {
      this.element.removeEventListener("hidden.bs.popover", this.boundHandlePopoverHidden)
      this.boundHandlePopoverHidden = null
    }
    if (this.boundHandleFocusIn) {
      this.element.removeEventListener("focusin", this.boundHandleFocusIn)
      this.boundHandleFocusIn = null
    }
    if (this.boundHandleFocusOut) {
      this.element.removeEventListener("focusout", this.boundHandleFocusOut)
      this.boundHandleFocusOut = null
    }
    if (this.boundHandleClick) {
      this.element.removeEventListener("click", this.boundHandleClick)
      this.boundHandleClick = null
    }
    if (this.boundHandleKeydown) {
      this.element.removeEventListener("keydown", this.boundHandleKeydown)
      this.boundHandleKeydown = null
    }

    this.#unbindPopoverTipListeners()
    this.#unbindDocumentDismissListeners()
  }

  #disposePopover() {
    if (!this.popover) return

    // Bootstrap's hide() queues an async transition-end callback that reads
    // _activeTrigger; dispose() nulls that property right away without
    // cancelling the pending callback. If Turbo tears this element down
    // mid-hide-animation, that callback later runs against a disposed
    // instance and throws (LUC-717). Defer disposal until Bootstrap itself
    // confirms the hide completed (hidden.bs.popover) rather than skipping
    // it outright -- Bootstrap's Data store is a live Map, not a WeakMap, so
    // an undisposed instance is retained for the rest of the page's life.
    if (this.hidePending) {
      const instance = this.popover
      this.popover = null
      this.element.addEventListener("hidden.bs.popover", () => instance.dispose(), { once: true })
      return
    }

    this.popover.dispose()
    this.popover = null
  }
}
